import { useCallback, useEffect, useRef, useState } from 'react';

// Headless portal-popover scaffolding, extracted verbatim from TypeaheadSelect:
// a menu portaled to document.body with viewport-flip placement, capture-phase
// scroll/resize repositioning (so it tracks inner scroll containers like the
// specs table), offscreen auto-close, and dual-ref (anchor + menu) outside-click
// close. Defaults reproduce TypeaheadSelect's original constants exactly.
//
// Returns: { open, setOpen, anchorRef, menuRef, menuStyle }
//   anchorRef  attach to the OUTERMOST trigger element — it is both the bounding-rect
//              anchor and the outside-click containment root (consolidates the
//              wrap+button refs TypeaheadSelect kept separate)
//   menuRef    attach to the portaled menu element
//   menuStyle  ready-to-spread fixed-position style (hidden until first measured)
export default function usePortalPopover({ gap = 6, margin = 10, desiredMax = 360, flipThreshold = 160 } = {}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // { top,left,width,maxHeight }
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  // Close on outside click. The menu is portaled to body, so test BOTH refs.
  useEffect(() => {
    const onDocMouseDown = (e) => {
      try {
        if (!open) return;
        const t = e.target;
        if (anchorRef.current && anchorRef.current.contains(t)) return;
        if (menuRef.current && menuRef.current.contains(t)) return;
        setOpen(false);
      } catch {}
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const updateMenuPos = useCallback(() => {
    const btn = anchorRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();

    // If the trigger is offscreen, close instead of leaving a floating menu.
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setOpen(false);
      return;
    }

    const belowTop = rect.bottom + gap;
    const belowAvail = window.innerHeight - belowTop - margin;
    const aboveAvail = rect.top - gap - margin;
    const shouldPlaceAbove = belowAvail < flipThreshold && aboveAvail > belowAvail;

    if (shouldPlaceAbove) {
      const maxHeight = Math.max(140, Math.min(desiredMax, aboveAvail));
      const top = Math.max(margin, rect.top - gap - maxHeight);
      setMenuPos({
        top,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin)),
        width: rect.width,
        maxHeight,
      });
      return;
    }

    const maxHeight = Math.max(140, Math.min(desiredMax, belowAvail));
    const top = Math.min(window.innerHeight - margin, belowTop);
    setMenuPos({
      top,
      left: Math.max(margin, Math.min(rect.left, window.innerWidth - rect.width - margin)),
      width: rect.width,
      maxHeight,
    });
  }, [gap, margin, desiredMax, flipThreshold]);

  // Keep the portal menu positioned on scroll/resize (including scroll containers).
  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  const menuStyle = {
    position: 'fixed',
    zIndex: 5000,
    top: menuPos?.top ?? 0,
    left: menuPos?.left ?? 0,
    width: menuPos?.width ?? undefined,
    maxHeight: menuPos?.maxHeight ?? undefined,
    visibility: menuPos ? 'visible' : 'hidden',
    pointerEvents: menuPos ? 'auto' : 'none',
  };

  return { open, setOpen, anchorRef, menuRef, menuStyle };
}
